import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, lazy, useEffect, useState } from "react";
import type { User } from "@supabase/supabase-js";
import {
  ArrowRight,
  Mic,
  Video,
  MicOff,
  PhoneOff,
  Maximize2,
} from "lucide-react";

import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/language-switcher";
import { ProgrammesSection } from "@/components/landing/programmes-section";
import { programmes } from "@/content/programmes";
import { pillars } from "@/content/approach";
import { PricingTeaser } from "@/components/landing/pricing-section";
import { FaqSection, faqs } from "@/components/landing/faq-section";
import { MobileCtaBar } from "@/components/landing/mobile-cta-bar";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { Reveal } from "@/components/reveal";
import { DeferMount } from "@/components/defer-mount";

const PlacementQuiz = lazy(() =>
  import("@/components/landing/placement-quiz").then((m) => ({ default: m.PlacementQuiz })),
);
const FacultySection = lazy(() =>
  import("@/components/landing/faculty-section").then((m) => ({ default: m.FacultySection })),
);
const TestimonialsCarousel = lazy(() =>
  import("@/components/landing/testimonials-carousel").then((m) => ({
    default: m.TestimonialsCarousel,
  })),
);
const ContactSection = lazy(() =>
  import("@/components/landing/contact-section").then((m) => ({ default: m.ContactSection })),
);

const SectionFallback = () => <div className="min-h-[320px]" aria-hidden="true" />;

import heroImage from "@/assets/hero-cohort-lesson.webp";
import hero640 from "@/assets/hero-cohort-lesson-640.webp";
import hero960 from "@/assets/hero-cohort-lesson-960.webp";
import logo from "@/assets/smart-word-labs-logo.png.asset.json";

const heroSrcSet = `${hero640} 640w, ${hero960} 960w, ${heroImage} 1280w`;
const heroSizes = "(max-width: 1024px) 100vw, 620px";

import { SITE_URL } from "@/lib/seo";


const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  "@id": `${SITE_URL}/#organization`,
  name: "Smart Word Labs",
  alternateName: "Smart Word Labs Online English",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}${logo.url}`,
    contentUrl: `${SITE_URL}${logo.url}`,
    width: 1920,
    height: 1047,
  },
  image: `${SITE_URL}${logo.url}`,
  description:
    "Premium live online English micro-cohorts for children aged 4-12, aligned to CEFR benchmarks.",
  areaServed: "Worldwide",
  aggregateRating: {
    "@type": "AggregateRating",
    itemReviewed: { "@id": `${SITE_URL}/#organization` },
    ratingValue: 4.9,
    bestRating: 5,
    worstRating: 1,
    ratingCount: 1200,
    reviewCount: 1200,
  },
  review: [
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Sana M." },
      reviewRating: {
        "@type": "Rating",
        ratingValue: 5,
        bestRating: 5,
        worstRating: 1,
      },
      reviewBody:
        "Aariz went from single-word answers to telling us full stories about his lesson. The cohort of four means he actually has to speak — that changed everything.",
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Priya N." },
      reviewRating: {
        "@type": "Rating",
        ratingValue: 5,
        bestRating: 5,
        worstRating: 1,
      },
      reviewBody:
        "Literacy Builders turned reading from a fight into something she asks for. Her writing at school jumped a whole level in one term.",
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Marco B." },
      reviewRating: {
        "@type": "Rating",
        ratingValue: 5,
        bestRating: 5,
        worstRating: 1,
      },
      reviewBody:
        "Confident Communicators is exactly what our son needed before secondary school — he now presents an argument at the dinner table without blinking.",
    },
  ],
};


const courseJsonLd = programmes.map((p) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  name: p.title,
  description: p.summary,
  url: `${SITE_URL}/programmes/${p.slug}`,
  educationalLevel: `CEFR ${p.cefr}`,
  typicalAgeRange: p.ageRange,
  inLanguage: "en",
  teaches: p.outcomes.join("; "),
  provider: { "@id": `${SITE_URL}/#organization` },
  hasCourseInstance: {
    "@type": "CourseInstance",
    courseMode: "Online",
    courseWorkload: p.workload,
  },
}));

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: "Smart Word Labs",
  alternateName: "Smart Word Labs Online English Classes",
  url: `${SITE_URL}/`,
  inLanguage: "en",
  publisher: { "@id": `${SITE_URL}/#organization` },
};

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f: { q: string; a: string }) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};


export const Route = createFileRoute("/")({
  head: () => {
    const ogImage = `${SITE_URL}${heroImage}`;
    return {
      meta: [
        { title: "Online English Classes for Kids & IELTS | Smart Word Labs" },
        { name: "description", content: "Live online English classes for kids 4-12 in micro-cohorts of 5, plus private IELTS coaching for all ages. Free assessment and learning plan." },
        { property: "og:title", content: "Online English Classes for Kids & IELTS | Smart Word Labs" },
        { property: "og:description", content: "Live online English classes for kids 4-12 in micro-cohorts of 5, plus private IELTS coaching for all ages. Free assessment and learning plan." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: SITE_URL },
        { property: "og:site_name", content: "Smart Word Labs" },
        { property: "og:image", content: ogImage },
        { property: "og:image:alt", content: "Young children speaking during a live small-group online English lesson with Smart Word Labs" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Online English Classes for Kids & IELTS | Smart Word Labs" },
        { name: "twitter:description", content: "Live online English classes for kids 4-12 in micro-cohorts of 5, plus private IELTS coaching for all ages. Free assessment and learning plan." },
        { name: "twitter:image", content: ogImage },
        { name: "twitter:image:alt", content: "Young children speaking during a live small-group online English lesson with Smart Word Labs" },
      ],
      links: [
        { rel: "canonical", href: `${SITE_URL}/` },
        {
          rel: "preload",
          as: "image",
          href: hero640,
          imageSrcSet: heroSrcSet,
          imageSizes: heroSizes,
          fetchPriority: "high",
        },

      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(organizationJsonLd) },
        { type: "application/ld+json", children: JSON.stringify(websiteJsonLd) },

        ...courseJsonLd.map((c) => ({
          type: "application/ld+json",
          children: JSON.stringify(c),
        })),
        { type: "application/ld+json", children: JSON.stringify(faqJsonLd) },
      ],
    };
  },
  component: Landing,
});


function Landing() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />


      <main className="pb-20 lg:pb-0">
        {/* Hero */}
        <section className="gradient-hero-animated">
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 lg:grid-cols-[1.05fr_1fr] lg:py-24">
            <div>
              <h1 className="text-4xl leading-[1.06] text-primary sm:text-5xl lg:text-[3.5rem]">
                {t("hero.title")}
              </h1>

              <div className="mt-6 flex animate-fade-in flex-wrap items-center gap-3 [animation-delay:120ms] [animation-fill-mode:backwards]">

                <span className="inline-flex items-center rounded-full border border-accent/40 bg-accent/10 px-4 py-1.5 text-sm font-bold text-primary">
                  {t("hero.rating")}
                </span>
              </div>

              <p className="mt-5 max-w-xl animate-fade-in text-lg leading-relaxed text-muted-foreground [animation-delay:220ms] [animation-fill-mode:backwards]">
                {t("hero.subtitleBefore")}{" "}
                <Link
                  to="/programmes"
                  className="font-semibold text-primary underline decoration-accent underline-offset-4 transition-colors hover:text-accent"
                >
                  {t("hero.subtitleLink")}
                </Link>
                .
              </p>

              <div className="mt-8 flex animate-fade-in flex-col gap-3 [animation-delay:320ms] [animation-fill-mode:backwards] sm:flex-row sm:flex-wrap sm:items-center">
                <Button
                  variant="hero"
                  size="xl"
                  className="shine-on-hover w-full rounded-xl text-base font-bold shadow-[var(--shadow-glow)] transition-transform hover:-translate-y-0.5 hover:scale-[1.02] sm:w-auto"
                  asChild

                >
                  <Link to="/book">{t("hero.ctaPrimary")}</Link>
                </Button>
                <Button
                  variant="outline"
                  size="xl"
                  className="w-full rounded-xl border-2 border-primary/30 bg-transparent text-base font-semibold text-primary hover:border-primary hover:bg-primary/5 hover:text-primary sm:w-auto"
                  asChild
                >
                  <Link to="/pricing">
                    {t("hero.ctaSecondary")}
                    <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>

                </Button>
              </div>
            </div>

            {/* Video-call style frame */}
            <div className="relative float-soft">
              <div className="animate-scale-in overflow-hidden rounded-2xl bg-primary p-2 shadow-[var(--shadow-card)] transition-transform duration-500 hover:-translate-y-1">
                <div className="flex items-center justify-between px-2 pb-2 pt-1">
                  <div className="flex items-center gap-1.5">
                    <span className="live-dot size-2.5 rounded-full bg-destructive/80" />
                    <span className="size-2.5 rounded-full bg-accent" />
                    <span className="size-2.5 rounded-full bg-chart-5" />

                  </div>
                  <span className="text-[11px] font-medium text-primary-foreground/60">
                    {t("hero.liveLesson")}
                  </span>
                  <span className="w-10" />
                </div>

                <div className="group relative overflow-hidden rounded-xl">
                  <img
                    src={heroImage}
                    srcSet={heroSrcSet}
                    sizes={heroSizes}
                    alt={t("hero.imageAlt")}

                    width={1280}
                    height={864}
                    fetchPriority="high"
                    decoding="async"
                    className="aspect-[16/11] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />

                  <div className="absolute end-3 top-3 w-1/4 overflow-hidden rounded-lg border-2 border-primary-foreground/30 bg-primary/60 px-2 py-6 text-center text-[10px] font-semibold text-primary-foreground/80 backdrop-blur">
                    Ms. Salma
                  </div>
                </div>

                <div className="flex items-center justify-between px-3 py-3">
                  <div className="flex items-center gap-3 text-primary-foreground/50">
                    <MicOff className="size-4" />
                    <Video className="size-4" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="grid size-8 place-items-center rounded-full bg-chart-3 text-primary-foreground">
                      <Video className="size-4" />
                    </span>
                    <span className="grid size-8 place-items-center rounded-full bg-chart-3 text-primary-foreground">
                      <Mic className="size-4" />
                    </span>
                    <span className="grid size-8 place-items-center rounded-full bg-destructive text-destructive-foreground">
                      <PhoneOff className="size-4" />
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-primary-foreground/50">
                    <Maximize2 className="size-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pillars */}
        <section id="approach" className="scroll-mt-24 bg-background py-20">
          <div className="mx-auto max-w-6xl px-5">
            <Reveal>
              <h2 className="text-center text-3xl text-primary sm:text-4xl">
                {t("approach.heading")}
              </h2>
            </Reveal>
            <div className="mt-12 grid gap-10 md:grid-cols-3">
              {pillars.map((p, i) => (
                <Reveal key={p.title} delay={i * 120} className="group">
                  <div className="flex items-start gap-4">
                    <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-secondary text-chart-3 transition-all duration-300 group-hover:bg-accent/20 group-hover:scale-110">
                      <p.icon className="size-5 transition-transform duration-300 group-hover:-rotate-6" />
                    </span>
                    <h3 className="min-w-0 text-xl leading-snug text-primary">{p.title}</h3>
                  </div>
                  <p className="mt-4 text-base leading-relaxed text-muted-foreground">{p.body}</p>
                </Reveal>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/how-it-works"
                className="group inline-flex items-center gap-2 text-base font-semibold text-primary transition-colors hover:text-accent"
              >
                {t("approach.link")}
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </section>

        <Reveal>
          <ProgrammesSection condensed />
        </Reveal>
        <DeferMount minHeight={640}>
          <Suspense fallback={<SectionFallback />}>
            <Reveal>
              <PlacementQuiz />
            </Reveal>
            <Reveal>
              <FacultySection />
            </Reveal>
          </Suspense>
        </DeferMount>
        <Reveal>
          <PricingTeaser />
        </Reveal>
        <DeferMount>
          <Suspense fallback={<SectionFallback />}>
            <Reveal>
              <TestimonialsCarousel />
            </Reveal>
          </Suspense>
        </DeferMount>
        <Reveal>
          <FaqSection />
        </Reveal>
        <DeferMount minHeight={520}>
          <Suspense fallback={<SectionFallback />}>
            <Reveal>
              <ContactSection />
            </Reveal>
          </Suspense>
        </DeferMount>



      </main>

      <SiteFooter />
      <MobileCtaBar />
    </div>
  );
}
